import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { createApiClient } from '../lib/api';

type Relevance = 'relevant' | 'irrelevant' | 'marginal';

function formatDuration(createdAt: string, endAt: string): string {
  const start = new Date(createdAt).getTime();
  const end = new Date(endAt).getTime();
  const diffMs = end - start;
  if (diffMs < 0 || isNaN(diffMs)) return '-';
  const totalSeconds = Math.floor(diffMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

export function ScanDetailPage() {
  const { scanSessionId } = useParams<{ scanSessionId: string }>();
  const { idToken } = useAuth();
  const api = createApiClient(idToken!);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['scan', scanSessionId],
    queryFn: () => api.getScanDetail(scanSessionId!),
    enabled: !!scanSessionId,
  });

  // Fetch existing reviews for this scan
  const { data: reviewsData } = useQuery({
    queryKey: ['comp-reviews', scanSessionId],
    queryFn: () => api.listCompReviews({ scanSessionId }),
    enabled: !!scanSessionId,
  });

  // Build a map of compUrl -> review
  const reviewMap: Record<string, { relevance: Relevance; notes?: string }> = {};
  if (reviewsData?.reviews) {
    for (const r of reviewsData.reviews) {
      reviewMap[r.compUrl] = { relevance: r.relevance, notes: r.notes };
    }
  }

  // Keyboard shortcuts
  const [focusedCompIndex, setFocusedCompIndex] = useState(-1);
  const comps = data?.deepResult?.valuation?.comps || [];
  const topCandidate = data?.fastResult?.candidates?.[0];

  const submitReviewMutation = useMutation({
    mutationFn: (review: { compUrl: string; relevance: Relevance; comp: any }) =>
      api.submitCompReview({
        scanSessionId: scanSessionId!,
        compUrl: review.compUrl,
        relevance: review.relevance,
        candidateBrand: topCandidate?.brand,
        candidateModel: topCandidate?.model,
        candidateCategory: data?.session?.resolvedCategory || data?.session?.categoryHint,
        compTitle: review.comp.title,
        compPrice: review.comp.price,
        compSource: review.comp.source,
        compPriceType: review.comp.priceType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comp-reviews', scanSessionId] });
    },
  });

  const handleReview = useCallback(
    (compUrl: string, relevance: Relevance, comp: any) => {
      submitReviewMutation.mutate({ compUrl, relevance, comp });
    },
    [submitReviewMutation]
  );

  // Keyboard navigation: j/k to navigate comps, 1/2/3 to score
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (comps.length === 0) return;

      if (e.key === 'j') {
        setFocusedCompIndex((i) => Math.min(i + 1, comps.length - 1));
      } else if (e.key === 'k') {
        setFocusedCompIndex((i) => Math.max(i - 1, 0));
      } else if (['1', '2', '3'].includes(e.key) && focusedCompIndex >= 0) {
        const comp = comps[focusedCompIndex];
        const relevance: Relevance = e.key === '1' ? 'relevant' : e.key === '2' ? 'marginal' : 'irrelevant';
        if (comp?.url) {
          handleReview(comp.url, relevance, comp);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [comps, focusedCompIndex, handleReview]);

  if (isLoading) return <div className="text-slate-400">Loading scan details...</div>;
  if (error) return <div className="text-red-400">Error: {(error as Error).message}</div>;
  if (!data) return <div className="text-slate-400">Scan not found</div>;

  const { session, fastResult, deepResult, images, messages, rawResults } = data;
  const valuation = deepResult?.valuation;
  const authenticity = deepResult?.authenticity;

  // Extract prompt strategy metadata from deep auth raw result
  const deepAuthRaw = rawResults?.find((r: any) => r.kind === 'deep');
  const deepAuthMeta = deepAuthRaw?.json?._meta as {
    promptStrategy?: string;
    promptSource?: string;
    categoryPromptUsed?: string;
  } | undefined;

  // Count reviews
  const reviewCount = Object.keys(reviewMap).length;
  const totalComps = comps.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-white text-sm">&larr; Back</button>
        <h1 className="text-xl font-semibold text-white">Scan Detail</h1>
        <span className="font-mono text-sm text-slate-500">{scanSessionId}</span>
      </div>

      {/* Session Info */}
      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
        <h2 className="text-sm font-medium text-slate-400 mb-3">Session</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div><span className="text-slate-500">Status:</span> <span className="text-white">{session.status}</span></div>
          <div><span className="text-slate-500">Category:</span> <span className="text-white">{session.resolvedCategory || session.categoryHint || '-'}</span></div>
          <div><span className="text-slate-500">Images:</span> <span className="text-white">{session.receivedImageCount}/{session.expectedImageCount}</span></div>
          <div><span className="text-slate-500">Created:</span> <span className="text-white">{new Date(session.createdAt).toLocaleString()}</span></div>
          <div>
            <span className="text-slate-500">Duration:</span>{' '}
            <span className="text-white font-mono">
              {(session.completedAt || session.updatedAt)
                ? formatDuration(session.createdAt, session.completedAt || session.updatedAt)
                : '-'}
            </span>
          </div>
        </div>
        {/* Prompt Strategy Debug Info */}
        {(session.enhancedAnalysis != null || deepAuthMeta) && (
          <div className="mt-3 pt-3 border-t border-slate-800 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-500">Prompt Strategy:</span>
            {session.enhancedAnalysis != null && (
              <span className={`px-2 py-0.5 text-xs rounded ${
                session.enhancedAnalysis
                  ? 'bg-purple-900/50 text-purple-300'
                  : 'bg-slate-800 text-slate-400'
              }`}>
                {session.enhancedAnalysis ? 'Enhanced' : 'Standard'}
              </span>
            )}
            {deepAuthMeta?.promptStrategy && (
              <span className={`px-2 py-0.5 text-xs rounded ${
                deepAuthMeta.promptStrategy === 'category_specific'
                  ? 'bg-blue-900/50 text-blue-300'
                  : 'bg-slate-800 text-slate-400'
              }`}>
                DeepAuth: {deepAuthMeta.promptStrategy}
              </span>
            )}
            {deepAuthMeta?.categoryPromptUsed && (
              <span className="px-2 py-0.5 text-xs rounded bg-green-900/50 text-green-300">
                Prompt: {deepAuthMeta.categoryPromptUsed}
              </span>
            )}
            {deepAuthMeta?.promptSource && (
              <span className="px-2 py-0.5 text-xs rounded bg-slate-800 text-slate-400">
                Source: {deepAuthMeta.promptSource}
              </span>
            )}
          </div>
        )}
      </section>

      {/* Photos */}
      {images && images.length > 0 && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Photos ({images.length})</h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {images.map((img: any) => (
              <a key={img.imageId} href={img.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <img
                  src={img.thumbnailUrl || img.url}
                  alt={`Image ${img.captureOrder}`}
                  className="h-32 w-32 object-cover rounded border border-slate-700 hover:border-orange-500 transition-colors"
                />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Fast Identify */}
      {fastResult && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Fast Identify</h2>
          {fastResult.candidates?.map((candidate: any, i: number) => (
            <div key={i} className="mb-4 last:mb-0">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-white font-medium">
                  {[candidate.brand, candidate.model, candidate.variant].filter(Boolean).join(' ')}
                </span>
                <span className="px-2 py-0.5 bg-orange-900/50 text-orange-300 text-xs rounded">
                  {(candidate.confidence * 100).toFixed(0)}%
                </span>
                {candidate.era && <span className="text-sm text-slate-500">{candidate.era}</span>}
              </div>
              {candidate.evidence && (
                <ul className="text-sm text-slate-400 space-y-1 ml-4">
                  {candidate.evidence.map((e: string, j: number) => (
                    <li key={j}>{e}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          <CollapsibleJson title="Raw Fast Result" data={fastResult} />
        </section>
      )}

      {/* Deep Authenticity */}
      {authenticity && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">
            Authenticity
            <span className="ml-2 px-2 py-0.5 bg-orange-900/50 text-orange-300 text-xs rounded">
              {(authenticity.authConfidence * 100).toFixed(0)}%
            </span>
          </h2>
          <div className="space-y-2">
            {authenticity.indicators?.map((ind: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className={`shrink-0 mt-0.5 w-2 h-2 rounded-full ${
                  ind.status === 'pass' ? 'bg-green-500' :
                  ind.status === 'warning' ? 'bg-amber-500' :
                  ind.status === 'fail' ? 'bg-red-500' : 'bg-slate-500'
                }`} />
                <div>
                  <span className="text-white">{ind.indicator}</span>
                  <span className="text-slate-500 ml-2">{ind.detail}</span>
                </div>
              </div>
            ))}
          </div>
          <CollapsibleJson title="Raw Authenticity Result" data={authenticity} />
        </section>
      )}

      {/* Deep Valuation & Comps with Review */}
      {valuation && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-slate-400">Valuation</h2>
            {totalComps > 0 && (
              <span className="text-xs text-slate-500">
                Reviewed: {reviewCount}/{totalComps}
                <span className="ml-2 text-slate-600">
                  (j/k navigate, 1=relevant, 2=marginal, 3=irrelevant)
                </span>
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
            {valuation.resaleRange && (
              <div>
                <div className="text-slate-500">Resale Range</div>
                <div className="text-white font-medium">
                  ${valuation.resaleRange.min} - ${valuation.resaleRange.max}
                </div>
              </div>
            )}
            {valuation.expectedPayRange && (
              <div>
                <div className="text-slate-500">Expected Pay</div>
                <div className="text-white">
                  ${valuation.expectedPayRange.min} - ${valuation.expectedPayRange.max}
                </div>
              </div>
            )}
            {valuation.liquidityScore != null && (
              <div>
                <div className="text-slate-500">Liquidity</div>
                <div className="text-white">{(valuation.liquidityScore * 100).toFixed(0)}%</div>
              </div>
            )}
            {valuation.pricingConfidence != null && (
              <div>
                <div className="text-slate-500">Pricing Confidence</div>
                <div className="text-white">{(valuation.pricingConfidence * 100).toFixed(0)}%</div>
              </div>
            )}
          </div>

          {valuation.searchTerms && (
            <div className="mb-4">
              <div className="text-xs text-slate-500 mb-1">Search Queries</div>
              <div className="flex flex-wrap gap-2">
                {valuation.searchTerms.map((term: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-slate-800 text-slate-300 text-xs rounded">{term}</span>
                ))}
              </div>
            </div>
          )}

          {comps.length > 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-2">Comparables ({comps.length})</div>
              <div className="space-y-1">
                {comps.map((comp: any, i: number) => {
                  const review = comp.url ? reviewMap[comp.url] : undefined;
                  const isFocused = i === focusedCompIndex;
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-3 text-sm border rounded px-3 py-2 transition-colors cursor-pointer ${
                        isFocused
                          ? 'border-orange-500 bg-slate-800/80'
                          : 'border-slate-800/50 bg-transparent hover:bg-slate-800/30'
                      }`}
                      onClick={() => setFocusedCompIndex(i)}
                    >
                      {/* Review buttons */}
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); comp.url && handleReview(comp.url, 'relevant', comp); }}
                          className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                            review?.relevance === 'relevant'
                              ? 'bg-green-600 text-white'
                              : 'bg-slate-800 text-slate-500 hover:bg-green-900 hover:text-green-300'
                          }`}
                          title="Relevant (1)"
                        >
                          Y
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); comp.url && handleReview(comp.url, 'marginal', comp); }}
                          className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                            review?.relevance === 'marginal'
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-800 text-slate-500 hover:bg-amber-900 hover:text-amber-300'
                          }`}
                          title="Marginal (2)"
                        >
                          ~
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); comp.url && handleReview(comp.url, 'irrelevant', comp); }}
                          className={`w-7 h-7 rounded text-xs font-bold transition-colors ${
                            review?.relevance === 'irrelevant'
                              ? 'bg-red-600 text-white'
                              : 'bg-slate-800 text-slate-500 hover:bg-red-900 hover:text-red-300'
                          }`}
                          title="Irrelevant (3)"
                        >
                          N
                        </button>
                      </div>

                      {/* Comp info */}
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-300 truncate">
                          {comp.url ? (
                            <a href={comp.url} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400">{comp.title}</a>
                          ) : comp.title}
                        </div>
                        <div className="flex gap-2 mt-0.5">
                          <span className="text-xs text-slate-500">{comp.source}</span>
                          <span className={`text-xs ${comp.priceType === 'sold' ? 'text-green-400' : 'text-blue-400'}`}>
                            {comp.priceType || 'asking'}
                          </span>
                          {comp.condition && <span className="text-xs text-slate-500">{comp.condition}</span>}
                          {comp.soldDate && <span className="text-xs text-slate-600">{new Date(comp.soldDate).toLocaleDateString()}</span>}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-white font-medium ml-4 shrink-0">${comp.price}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <CollapsibleJson title="Raw Valuation Result" data={valuation} />
        </section>
      )}

      {/* Messages Timeline */}
      {messages && messages.length > 0 && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Pipeline Messages ({messages.length})</h2>
          <div className="space-y-2">
            {messages.map((msg: any, i: number) => (
              <div key={i} className="text-sm border-l-2 border-slate-700 pl-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">#{msg.seq}</span>
                  <span className="text-xs px-1.5 py-0.5 bg-slate-800 rounded text-slate-400">{msg.type}</span>
                  <span className="text-xs text-slate-600">{new Date(msg.createdAt).toLocaleTimeString()}</span>
                </div>
                <CollapsibleJson title="Payload" data={msg.payload} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Raw Results */}
      {rawResults && rawResults.length > 0 && (
        <section className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h2 className="text-sm font-medium text-slate-400 mb-3">Raw Scan Results</h2>
          {rawResults.map((result: any, i: number) => (
            <CollapsibleJson key={i} title={`${result.kind} (${result.versionKind})`} data={result} />
          ))}
        </section>
      )}
    </div>
  );
}

function CollapsibleJson({ title, data }: { title: string; data: any }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1"
      >
        <span>{open ? '\u25BC' : '\u25B6'}</span>
        {title}
      </button>
      {open && (
        <pre className="mt-1 text-xs text-slate-400 bg-slate-950 border border-slate-800 rounded p-3 overflow-x-auto max-h-96 overflow-y-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
